import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileQuestion,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  QUESTION_DIFFICULTY_LABEL,
  QUESTION_DIFFICULTY_ORDER,
  QUESTION_LANGUAGE_LABEL,
  QUESTION_TYPE_LABEL,
  QUESTION_TYPE_ORDER,
  type AssessmentQuestionPublic,
  type AssessmentQuestionStatus,
  type QuestionDifficulty,
  type QuestionLanguage,
  type QuestionType,
} from "@eduviaos/shared";
import { mapQuestionStatusToPill } from "@/lib/assessments/statusPill";
import {
  useApproveAssessmentQuestion,
  useArchiveAssessmentQuestion,
  useAssessmentQuestions,
  useAssessmentTopics,
} from "@/hooks/useAssessments";
import { useCanAuthorAssessments } from "@/hooks/useAssessmentAuthoring";
import { TenantAuthoringPausedBanner } from "@/components/assessments/TenantAuthoringPausedBanner";
import { AIFillDialog } from "@/components/assessments/AIFillDialog";

/**
 * Phase H5 FE-1 — IA Assessments → Question bank.
 *
 * Read-first DataTable + drawer viewer over `/assessments/questions`.
 * Authoring (create / edit) for the 14 polymorphic types is deferred
 * to a follow-up (Iron Rule #17 — listed in the PR's Deferred section).
 * The actions surfaced today are the ones that block the H7 fresh-
 * school walkthrough: approve (so a question becomes drawable) and
 * archive (so a flawed question never ships into an attempt).
 *
 * Filters compose AND-style and round-trip to the BE so very large
 * banks remain navigable without paginated client-side state.
 */

interface FilterState {
  topicSlug: string | "__any__";
  type: QuestionType | "__any__";
  difficulty: QuestionDifficulty | "__any__";
  language: QuestionLanguage | "__any__";
  status: AssessmentQuestionStatus | "__any__";
  search: string;
}

const EMPTY_FILTERS: FilterState = {
  topicSlug: "__any__",
  type: "__any__",
  difficulty: "__any__",
  language: "__any__",
  status: "__any__",
  search: "",
};

export function AssessmentQuestionBankPage() {
  const [filters, setFilters] = React.useState<FilterState>(EMPTY_FILTERS);
  const [viewingId, setViewingId] = React.useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState<AssessmentQuestionPublic | null>(null);
  const [archiveReason, setArchiveReason] = React.useState("");
  const [archiveError, setArchiveError] = React.useState<string | null>(null);
  const [aiFillOpen, setAiFillOpen] = React.useState(false);

  const topicsQuery = useAssessmentTopics();
  const questionsQuery = useAssessmentQuestions({
    topic_slug: filters.topicSlug === "__any__" ? undefined : filters.topicSlug,
    type: filters.type === "__any__" ? undefined : filters.type,
    difficulty: filters.difficulty === "__any__" ? undefined : filters.difficulty,
    language: filters.language === "__any__" ? undefined : filters.language,
    status: filters.status === "__any__" ? undefined : filters.status,
    include_archived: filters.status === "archived",
    limit: 200,
  });

  const approveMutation = useApproveAssessmentQuestion();
  const archiveMutation = useArchiveAssessmentQuestion();

  // Phase H5d — RBAC + per-tenant authoring flag, single source of
  // truth in `useCanAuthorAssessments`.
  const { canAuthor: canWrite, pausedByTenant } = useCanAuthorAssessments();

  const allItems = React.useMemo(() => questionsQuery.data?.items ?? [], [questionsQuery.data]);
  const total = questionsQuery.data?.total ?? 0;

  const items = React.useMemo(() => {
    const trimmed = filters.search.trim().toLowerCase();
    if (!trimmed) return allItems;
    return allItems.filter(
      (q) =>
        q.prompt_richtext.toLowerCase().includes(trimmed) ||
        q.tags.some((t) => t.toLowerCase().includes(trimmed)),
    );
  }, [allItems, filters.search]);

  const viewingQuestion = React.useMemo(
    () => allItems.find((q) => q.id === viewingId) ?? null,
    [allItems, viewingId],
  );

  const handleApprove = async (q: AssessmentQuestionPublic) => {
    try {
      await approveMutation.mutateAsync({ id: q.id });
      toast.success(`Approved "${truncatePrompt(q.prompt_richtext)}".`);
    } catch (raw) {
      toast.error(extractMessage(raw, "Could not approve — please try again."));
    }
  };

  const handleConfirmArchive = async () => {
    if (!pendingArchive) return;
    setArchiveError(null);
    if (archiveReason.trim().length < 3) {
      setArchiveError("Reason must be at least 3 characters.");
      return;
    }
    try {
      await archiveMutation.mutateAsync({
        id: pendingArchive.id,
        reason: archiveReason.trim(),
      });
      toast.success(`Archived "${truncatePrompt(pendingArchive.prompt_richtext)}".`);
      setPendingArchive(null);
      setArchiveReason("");
    } catch (raw) {
      setArchiveError(extractMessage(raw, "Could not archive — please try again."));
    }
  };

  const activeFilterCount =
    (filters.topicSlug !== "__any__" ? 1 : 0) +
    (filters.type !== "__any__" ? 1 : 0) +
    (filters.difficulty !== "__any__" ? 1 : 0) +
    (filters.language !== "__any__" ? 1 : 0) +
    (filters.status !== "__any__" ? 1 : 0);

  return (
    <div className="mx-auto max-w-7xl py-8" data-testid="question-bank">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/assessments">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Assessments
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Question bank</PageHeaderHeading>
            <PageHeaderDescription>
              Every question available to the blueprint engine. Approve drafts to make them
              drawable, archive flawed ones to take them out of rotation.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            {canWrite && !pausedByTenant ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => setAiFillOpen(true)}
                disabled={filters.topicSlug === "__any__"}
                aria-label={
                  filters.topicSlug === "__any__"
                    ? "Pick a topic in the filter to enable AI Fill"
                    : "Open AI Fill dialog"
                }
                title={
                  filters.topicSlug === "__any__"
                    ? "Pick a topic in the filter to enable AI Fill"
                    : undefined
                }
                data-testid="ai-fill-open"
              >
                <Sparkles aria-hidden="true" className="size-4" />
                AI Fill
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void questionsQuery.refetch()}
              disabled={questionsQuery.isFetching}
              aria-label="Refresh questions"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", questionsQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {pausedByTenant ? (
        <div className="mt-4">
          <TenantAuthoringPausedBanner
            body={
              <>
                <span className="font-medium">Question authoring is paused.</span> Your account
                manager has temporarily disabled creating, editing, or approving questions for this
                tenant. You can still browse the question bank and reference existing items. Contact
                your account manager to re-enable authoring.
              </>
            }
          />
        </div>
      ) : null}

      {questionsQuery.isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load questions
            {questionsQuery.error instanceof Error && questionsQuery.error.message
              ? `: ${questionsQuery.error.message}`
              : "."}
          </span>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              placeholder="Search prompt or tag…"
              className="pl-8"
              aria-label="Filter questions by prompt or tag"
            />
          </div>
          <Select
            value={filters.topicSlug}
            onValueChange={(v) => setFilters((p) => ({ ...p, topicSlug: v }))}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Topic filter">
              <SelectValue placeholder="Any topic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any topic</SelectItem>
              {(topicsQuery.data?.items ?? []).map((t) => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.type}
            onValueChange={(v) => setFilters((p) => ({ ...p, type: v as FilterState["type"] }))}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Type filter">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any type</SelectItem>
              {QUESTION_TYPE_ORDER.map((t) => (
                <SelectItem key={t} value={t}>
                  {QUESTION_TYPE_LABEL[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.difficulty}
            onValueChange={(v) =>
              setFilters((p) => ({ ...p, difficulty: v as FilterState["difficulty"] }))
            }
          >
            <SelectTrigger className="h-9 w-36" aria-label="Difficulty filter">
              <SelectValue placeholder="Any difficulty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any difficulty</SelectItem>
              {QUESTION_DIFFICULTY_ORDER.map((d) => (
                <SelectItem key={d} value={d}>
                  {QUESTION_DIFFICULTY_LABEL[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status}
            onValueChange={(v) => setFilters((p) => ({ ...p, status: v as FilterState["status"] }))}
          >
            <SelectTrigger className="h-9 w-32" aria-label="Status filter">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          {activeFilterCount > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
              <X aria-hidden="true" className="size-3.5" />
              Clear ({activeFilterCount})
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36%]">Prompt</TableHead>
                <TableHead className="w-[15%]">Type</TableHead>
                <TableHead className="w-[11%]">Topic</TableHead>
                <TableHead className="w-[9%]">Difficulty</TableHead>
                <TableHead className="w-[7%]">Grades</TableHead>
                <TableHead className="w-[6%]">Lang</TableHead>
                <TableHead className="w-[9%]">Status</TableHead>
                <TableHead className="w-[7%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {questionsQuery.isLoading ? (
              <DataTableSkeleton rows={6} columns={8} />
            ) : (
              <TableBody>
                {items.map((q) => (
                  <QuestionRow
                    key={q.id}
                    q={q}
                    canWrite={canWrite}
                    onView={(id) => setViewingId(id)}
                    onApprove={(target) => void handleApprove(target)}
                    isApprovePending={
                      approveMutation.isPending && approveMutation.variables?.id === q.id
                    }
                    onArchive={(t) => {
                      setArchiveError(null);
                      setArchiveReason("");
                      setPendingArchive(t);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!questionsQuery.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<FileQuestion aria-hidden="true" />}
            title={
              activeFilterCount > 0 || filters.search
                ? "No questions match your filters"
                : "No questions yet"
            }
            description={
              activeFilterCount > 0 || filters.search
                ? "Adjust the filters or clear them to see the full bank."
                : "Questions are authored from the BE today (Phase F7) or AI-generated per topic. Pick a topic in the filter and click \u2018AI Fill\u2019 to generate drafts, or seed the bank via the BE routes."
            }
            action={
              activeFilterCount > 0 || filters.search ? (
                <Button variant="outline" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : null}

        {!questionsQuery.isLoading && items.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {items.length.toLocaleString()} shown
              {filters.search ? " (filtered)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">Authoring note:</strong> the 14 polymorphic question
          types &mdash; including MCQ, fill-in-the-blank, code, match-following, file upload &mdash;
          are authorable via the BE today. A type-aware author UI is a follow-up slice; for now seed
          the bank via the BE routes or use <strong className="text-foreground">AI Fill</strong> per
          topic.
        </CardContent>
      </Card>

      {aiFillOpen && filters.topicSlug !== "__any__"
        ? (() => {
            const topic = (topicsQuery.data?.items ?? []).find((t) => t.slug === filters.topicSlug);
            if (!topic) return null;
            return (
              <AIFillDialog
                open={aiFillOpen}
                onOpenChange={setAiFillOpen}
                topic={topic}
                onSuccess={(n) =>
                  toast.success(
                    `Generated ${n} draft question${n === 1 ? "" : "s"} for ${topic.name}.`,
                  )
                }
              />
            );
          })()
        : null}

      <QuestionViewer
        question={viewingQuestion}
        topicName={
          (topicsQuery.data?.items ?? []).find((t) => t.slug === viewingQuestion?.topic_slug)
            ?.name ?? null
        }
        onClose={() => setViewingId(null)}
      />

      <Dialog
        open={Boolean(pendingArchive)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(null);
            setArchiveError(null);
            setArchiveReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive question?</DialogTitle>
            <DialogDescription>
              The question is removed from new attempts. Existing attempts retain their snapshot, so
              historical results remain renderable. A reason is mandatory — it lands in the audit
              log.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="archive-q-reason">Reason (3–500 chars)</Label>
            <Textarea
              id="archive-q-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Authoring error: option B is also correct."
              maxLength={500}
              rows={3}
            />
          </div>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive question
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionRow({
  q,
  canWrite,
  onView,
  onApprove,
  onArchive,
  isApprovePending,
}: {
  q: AssessmentQuestionPublic;
  canWrite: boolean;
  onView: (id: string) => void;
  onApprove: (q: AssessmentQuestionPublic) => void;
  onArchive: (q: AssessmentQuestionPublic) => void;
  isApprovePending: boolean;
}) {
  const status = mapQuestionStatusToPill(q.status, q.deleted_at);
  const isArchived = q.deleted_at !== null || q.status === "archived";
  const isDraft = q.status === "draft" && !isArchived;
  return (
    <TableRow>
      <TableCell>
        <button
          type="button"
          className="text-left hover:underline focus-visible:underline focus-visible:outline-none"
          onClick={() => onView(q.id)}
        >
          <span className="text-foreground line-clamp-2 text-sm">
            {truncatePrompt(q.prompt_richtext, 200)}
          </span>
          {q.tags.length > 0 ? (
            <span className="text-muted-foreground mt-1 line-clamp-1 text-[10px]">
              {q.tags.map((t) => `#${t}`).join(" · ")}
            </span>
          ) : null}
        </button>
      </TableCell>
      <TableCell className="text-xs">{QUESTION_TYPE_LABEL[q.type]}</TableCell>
      <TableCell className="text-muted-foreground font-mono text-[11px]">
        {q.topic_slug ?? "—"}
      </TableCell>
      <TableCell className="text-xs">{QUESTION_DIFFICULTY_LABEL[q.difficulty]}</TableCell>
      <TableCell className="text-xs tabular-nums">
        {q.grade_band.min}–{q.grade_band.max}
      </TableCell>
      <TableCell className="text-xs">{q.language.toUpperCase()}</TableCell>
      <TableCell>
        <StatusPill status={status} />
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Open question actions"
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onView(q.id)}>
              <FileQuestion aria-hidden="true" className="size-3.5" />
              View details
            </DropdownMenuItem>
            {canWrite && isDraft ? (
              <DropdownMenuItem onSelect={() => onApprove(q)} disabled={isApprovePending}>
                <CheckCircle2 aria-hidden="true" className="size-3.5" />
                {isApprovePending ? "Approving…" : "Approve"}
              </DropdownMenuItem>
            ) : null}
            {canWrite && !isArchived ? (
              <DropdownMenuItem
                onSelect={() => onArchive(q)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                Archive…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

function QuestionViewer({
  question,
  topicName,
  onClose,
}: {
  question: AssessmentQuestionPublic | null;
  topicName: string | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={Boolean(question)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-2xl flex-col gap-4 overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Question details</SheetTitle>
          <SheetDescription>
            Read-only view. Approving / archiving lives in the table's action menu.
          </SheetDescription>
        </SheetHeader>
        {question ? (
          <div className="grid gap-4 text-sm">
            <div className="grid gap-1">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                {QUESTION_TYPE_LABEL[question.type]} ·{" "}
                {QUESTION_DIFFICULTY_LABEL[question.difficulty]} ·{" "}
                {QUESTION_LANGUAGE_LABEL[question.language]}
              </span>
              <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                {question.prompt_richtext}
              </p>
              {question.prompt_latex ? (
                <pre className="bg-muted/40 mt-1 overflow-x-auto rounded-md px-2 py-1 text-xs">
                  {question.prompt_latex}
                </pre>
              ) : null}
              {question.prompt_image_url ? (
                <a
                  href={question.prompt_image_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline"
                >
                  Prompt image →
                </a>
              ) : null}
            </div>

            <div className="border-border/60 grid gap-2 rounded-md border p-3">
              <span className="text-muted-foreground text-xs font-medium uppercase">Metadata</span>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <StatusPill
                    status={mapQuestionStatusToPill(question.status, question.deleted_at)}
                  />
                </dd>
                <dt className="text-muted-foreground">Topic</dt>
                <dd className="font-mono">
                  {topicName ?? "—"}{" "}
                  {question.topic_slug ? (
                    <span className="text-muted-foreground">({question.topic_slug})</span>
                  ) : null}
                </dd>
                <dt className="text-muted-foreground">Grade band</dt>
                <dd className="tabular-nums">
                  {question.grade_band.min}–{question.grade_band.max}
                </dd>
                <dt className="text-muted-foreground">Marks</dt>
                <dd className="tabular-nums">
                  +{question.marks}
                  {question.negative_marks > 0 ? ` / −${question.negative_marks}` : ""}
                </dd>
                <dt className="text-muted-foreground">Origin</dt>
                <dd className="capitalize">{question.origin}</dd>
                <dt className="text-muted-foreground">Approved at</dt>
                <dd>{question.approved_at ?? "—"}</dd>
              </dl>
              {question.tags.length > 0 ? (
                <div className="text-muted-foreground mt-1 text-[11px]">
                  Tags: {question.tags.map((t) => `#${t}`).join(" · ")}
                </div>
              ) : null}
            </div>

            {question.options.length > 0 ? (
              <div className="border-border/60 grid gap-2 rounded-md border p-3">
                <span className="text-muted-foreground text-xs font-medium uppercase">Options</span>
                <ol className="grid gap-1.5">
                  {question.options.map((opt, idx) => (
                    <li
                      key={opt.id}
                      className={cn(
                        "border-border/40 grid gap-0.5 rounded-md border px-2.5 py-1.5",
                        opt.is_correct &&
                          "border-emerald-500/40 bg-emerald-500/5 dark:bg-emerald-500/10",
                      )}
                    >
                      <span className="text-foreground text-xs">
                        {String.fromCharCode(65 + idx)}.{" "}
                        {opt.label_richtext ?? opt.latex ?? opt.image_url ?? "—"}
                        {opt.is_correct ? (
                          <ShieldCheck
                            aria-hidden="true"
                            className="ml-1 inline size-3 text-emerald-600 dark:text-emerald-400"
                          />
                        ) : null}
                      </span>
                      <span className="text-muted-foreground font-mono text-[10px]">
                        id: {opt.id}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            <div className="border-border/60 grid gap-2 rounded-md border p-3">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Correct answer ({question.correct_answer.answer_kind})
              </span>
              <pre className="bg-muted/40 overflow-x-auto rounded-md px-2 py-1 text-[11px]">
                {JSON.stringify(question.correct_answer, null, 2)}
              </pre>
            </div>

            {question.solution_richtext ? (
              <div className="border-border/60 grid gap-2 rounded-md border p-3">
                <span className="text-muted-foreground text-xs font-medium uppercase">
                  Solution
                </span>
                <p className="text-foreground whitespace-pre-wrap text-sm">
                  {question.solution_richtext}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function truncatePrompt(html: string, max = 80): string {
  const plain = html.replace(/<[^>]+>/g, "").trim();
  return plain.length <= max ? plain : `${plain.slice(0, max - 1)}…`;
}

function extractMessage(raw: unknown, fallback: string): string {
  if (raw instanceof ApiError && raw.message) return raw.message;
  if (raw instanceof Error && raw.message) return raw.message;
  return fallback;
}
